import { Text } from "@certn/cascadia";
import {
  Card,
  DefaultTag,
  Divider,
  SectionMessage,
} from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";

import {
  getCurrencySymbol,
  getShowAutoCollectAlert,
  numberFormatter,
} from "./utils";
import { InvoiceProps } from "../../../../src/schemas-and-types";

dayjs.extend(utc);

type InvoiceHeaderProps = {
  invoiceData: InvoiceProps["invoiceData"];
  isClient?: boolean;
};

export const Header = (props: InvoiceHeaderProps) => {
  const { t } = useTranslation();
  const { invoiceData, isClient } = props;

  const { title, number } = invoiceData?.billing_profile?.payment_profile || {};
  const formattedCollectAtDate = formatDate(
    invoiceData.collect_at,
    "MMM dd, yyyy"
  );
  const shouldShowAutoCollectAlert = getShowAutoCollectAlert(invoiceData);

  const currencySymbol = getCurrencySymbol(invoiceData.currency);

  const fields: { title: string; value: string; testIdPrefix: string }[] = [
    {
      title: t("Invoice number"),
      value: `#${invoiceData.invoice_number}` || "--",
      testIdPrefix: "sm-invoice-details-invoice-number",
    },
    {
      title: t("Billing period"),
      value: `${dayjs
        .utc(invoiceData.period_start, "YYYY-MM-DD")
        .format("MMM D, YYYY")} - ${dayjs
        .utc(invoiceData.period_end, "YYYY-MM-DD")
        .subtract(1, "day")
        .format("MMM D, YYYY")}`,
      testIdPrefix: "sm-invoice-details-period",
    },
    {
      title: t("Issue date"),
      value: dayjs
        .utc(invoiceData.issue_date, "YYYY-MM-DD")
        .format("MMM D, YYYY"),
      testIdPrefix: "sm-invoice-details-issue-date",
    },
    ...(isClient
      ? [
          {
            title: t("Due date"),
            value: dayjs
              .utc(invoiceData.due_date, "YYYY-MM-DD")
              .format("MMM D, YYYY"),
            testIdPrefix: "sm-invoice-details-due-date",
          },
          {
            title: t("PO number"),
            value: invoiceData.billing_profile.purchase_order_number || "",
            testIdPrefix: "sm-invoice-details-po-number",
          },
        ]
      : []),
  ];

  return (
    <>
      <Card border="dropshadow" className={styles.card}>
        {fields
          .filter((field) => !!field.value) // Filter out empties
          .map((field, i) => (
            <div key={i} className={css(styles.row)}>
              <Text
                style="global/heading-medium"
                testID={`${field.testIdPrefix}-label`}
              >
                {/* FIXME: this raises a i18next parser warning Key is not a string literal */}
                {t(field.title)}
              </Text>
              <Text
                style="global/body-primary"
                testID={`${field.testIdPrefix}-value`}
              >
                {field.value}
              </Text>
            </div>
          ))}
        <div className={styles.highlighted}>
          <div className={styles.row}>
            <Text style="global/heading-medium">{t("Status")}</Text>
            <DefaultTag size="medium">
              <Text
                style="global/heading-medium"
                color="text-inverse"
                className={styles.status}
              >
                {invoiceData.status}
              </Text>
            </DefaultTag>
          </div>
          <div className={styles.row}>
            <Text style="global/heading-medium">{t("Total")}</Text>
            <Text style="global/metric-small">
              {currencySymbol}
              {numberFormatter(invoiceData.total)} {invoiceData.currency}
            </Text>
          </div>
          <div className={styles.row}>
            <Text style="global/heading-medium">{t("Balance")}</Text>
            <Text style="global/metric-small">
              {currencySymbol}
              {invoiceData.balance
                ? numberFormatter(invoiceData.balance)
                : numberFormatter(invoiceData.total)}{" "}
              {invoiceData.currency}
            </Text>
          </div>
        </div>
      </Card>
      {shouldShowAutoCollectAlert && (
        <div className={styles.sectionMessageContainer}>
          <SectionMessage
            icon="info"
            type="info"
            sectionTitle={t("Automatic payment collection")}
          >
            <div className={styles.autoCollectAlert}>
              <Text style="global/body-primary">
                {t(
                  "Certn will automatically collect payment from your credit card on {{formattedCollectAtDate}}.",
                  { formattedCollectAtDate }
                )}
              </Text>
              <Divider hideBorder marginTop="space-2" marginBottom="space-2" />
              <Text style="global/body-primary">
                {t("Payment profile: {{title}}", { title })}
              </Text>
              <Text style="global/body-primary">
                {t("Card ending in: **{{number}}", { number })}
              </Text>
            </div>
          </SectionMessage>
        </div>
      )}
    </>
  );
};

const styles = {
  card: css({}),
  row: css({
    padding: `0 ${"var(--space-3)"}`,
    display: "flex",
    alignItems: "end",
    textAlign: "right",
    justifyContent: "space-between",
    "&:first-child": {
      paddingTop: "var(--space-2)",
    },
    "&:last-child": {
      paddingBottom: "var(--space-2)",
    },
  }),
  highlighted: css({
    marginTop: "var(--space-3)",
    backgroundColor: "var(--background-accent-tofino-weak)",
  }),
  status: css({
    minWidth: "6em",
    textAlign: "center",
    display: "inline-block",
  }),
  autoCollectAlert: css({
    display: "flex",
    flexDirection: "column",
  }),
  sectionMessageContainer: css({
    marginTop: "var(--space-3)",
  }),
};
